import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Email as EmailIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as ContentCopyIcon,
  CheckCircle as CheckCircleIcon,
  Key as KeyIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { showSuccess, showError } from '../../../Components/reuse/Toast';
import useCallOnce from '../../../hooks/useCallOnce';
import { getEmailSettings, createEmailSettings, sendTestEmail } from '../../../redux/actions/emailActions';
import outlookicon from '../../../images/outlookIcon.png';

const OutlookIntegration = () => {
  const dispatch = useDispatch();
  const { emailData, loading, error, validationErrors, lastMessage, sendingTestEmail } = useSelector((state) => state.email);

  const [connectionModalOpen, setConnectionModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ clientId: '', clientSecret: '', tenantId: '', senderEmail: '' });
  const [formErrors, setFormErrors] = useState({});
  const [showClientSecret, setShowClientSecret] = useState(false);
  const [showClientId, setShowClientId] = useState(false);
  const [showTenantId, setShowTenantId] = useState(false);
  const [isRefreshingConnection, setIsRefreshingConnection] = useState(false);

  useCallOnce('integration/outlook/check', () => {
    dispatch(getEmailSettings());
  }, [dispatch]);

  useEffect(() => {
    if (lastMessage && !error && connectionModalOpen) {
      setConnectionModalOpen(false);
      setFormData({ clientId: '', clientSecret: '', tenantId: '', senderEmail: '' });
      setFormErrors({});
      if (!isRefreshingConnection) {
        setIsRefreshingConnection(true);
        setTimeout(() => {
          dispatch(getEmailSettings());
          setIsRefreshingConnection(false);
        }, 1000);
      }
    }
  }, [lastMessage, error, connectionModalOpen, dispatch, isRefreshingConnection]);

  useEffect(() => {
    if (validationErrors && connectionModalOpen) {
      const apiFieldErrors = mapApiValidationErrors(validationErrors);
      setFormErrors(apiFieldErrors);
    }
  }, [validationErrors, connectionModalOpen]);

  const outlookConnected = !!(emailData && emailData.outlook && (emailData.outlook.clientId || emailData.outlook.senderEmail));
  
  // Check if Gmail is connected
  const gmailConnected = !!(emailData && emailData.gmail && emailData.gmail.connected);

  const validateForm = () => {
    const errors = {};
    if (!formData.clientId.trim()) errors.clientId = 'Client ID is required';
    if (!formData.clientSecret.trim()) errors.clientSecret = 'Client Secret is required';
    if (!formData.tenantId.trim()) errors.tenantId = 'Tenant ID is required';
    if (!formData.senderEmail.trim()) {
      errors.senderEmail = 'Sender Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.senderEmail)) {
      errors.senderEmail = 'Please enter a valid email address';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    const payload = {
      provider: 'outlook',
      outlook: {
        clientId: formData.clientId,
        clientSecret: formData.clientSecret,
        tenantId: formData.tenantId,
        senderEmail: formData.senderEmail,
      },
    };
    dispatch(createEmailSettings(payload));
  };

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
    if (formErrors[field]) setFormErrors((prev) => ({ ...prev, [field]: '' }));
    if (validationErrors && validationErrors.some((err) => err.path === field)) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showSuccess('Copied to clipboard!');
  };

  const toggleVisibility = (field) => {
    if (field === 'clientSecret') setShowClientSecret(!showClientSecret);
    if (field === 'clientId') setShowClientId(!showClientId);
    if (field === 'tenantId') setShowTenantId(!showTenantId);
  };

  const handleOutlookSetup = () => {
    // Check if Gmail is already connected
    if (gmailConnected) {
      showError('Remove Gmail to connect Outlook integration');
      return;
    }
    
    setConnectionModalOpen(true);
    setFormData({ clientId: '', clientSecret: '', tenantId: '', senderEmail: '' });
    setFormErrors({});
  };

  const handleCloseModal = () => {
    setConnectionModalOpen(false);
    setFormData({ clientId: '', clientSecret: '', tenantId: '', senderEmail: '' });
    setFormErrors({});
  };

  const mapApiValidationErrors = (apiErrors) => {
    if (!apiErrors || !Array.isArray(apiErrors)) return {};
    const fieldErrors = {};
    apiErrors.forEach((error) => {
      if (error.path && error.msg) fieldErrors[error.path] = error.msg;
    });
    return fieldErrors;
  };

  return (
    <Box>
      <Card
        elevation={2}
        sx={{
          borderRadius: 2,
          bgcolor: '#ffffff',
          border: '1px solid #e5e7eb',
          height: '100%',
          minHeight: 350,
          cursor: outlookConnected ? 'default' : 'pointer',
          '&:hover': !outlookConnected ? { boxShadow: 4, borderColor: '#6b7280' } : {},
          transition: 'all 0.2s ease-in-out',
        }}
        onClick={!outlookConnected ? handleOutlookSetup : undefined}
      >
        <CardContent sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <Box>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: outlookConnected ? '#f0fdf4' : '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3, border: outlookConnected ? '2px solid #bbf7d0' : 'none' }}>
              {/* <Box component="img" src="https://upload.wikimedia.org/wikipedia/commons/d/df/Microsoft_Office_Outlook_%282018%E2%80%93present%29.svg" alt="Outlook" sx={{ width: 36, height: 36, objectFit: 'contain', filter: outlookConnected ? 'none' : 'grayscale(1) opacity(0.6)' }} /> */}
              <img src={outlookicon} alt="Outlook" style={{ width: 64, height: 64, borderRadius: '8px' }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: outlookConnected ? '#16a34a' : '#111827' }}>
              Outlook
            </Typography>
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {outlookConnected ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, bgcolor: '#f0fdf4', borderRadius: 2, border: '2px solid #bbf7d0' }}>
                  <Typography variant="body2" sx={{ color: '#15803d', fontWeight: 600, fontSize: '14px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Connected
                  </Typography>
                </Box>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                  <Button variant="text" size="small" onClick={() => setDetailsModalOpen(true)} sx={{ color: '#6b7280', '&:hover': { backgroundColor: '#f3f4f6' } }}>
                    View Connection Details
                  </Button>
                </Box>
                {/* <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
                  <Button variant="text" size="small" sx={{ color: '#ffffff', backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#d32f2f' } }}>
                    Disconnect
                  </Button>
                </Box> */}
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ mb: 2, color: '#6b7280' }}>
                  Connect your Outlook email to send automated notifications and communications.
                </Typography>
                <Typography variant="body2" sx={{ color: '#6b7280' }}>
                  Click to configure Outlook integration settings
                </Typography>
              </>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Configure Outlook Dialog */}
      <Dialog open={connectionModalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #e5e7eb', pb: 2, mb: 0 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#111827', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <EmailIcon sx={{ fontSize: 20, color: '#ffffff' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Configure Outlook Integration
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Grid container pt={4} spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="App Client ID"
                type={showClientId ? 'text' : 'password'}
                value={formData.clientId}
                onChange={handleChange('clientId')}
                placeholder="Enter your App Client ID"
                error={!!formErrors.clientId}
                helperText={formErrors.clientId}
                InputProps={{
                  startAdornment: <KeyIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton onClick={() => toggleVisibility('clientId')} edge="end" size="small">
                        {showClientId ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      {formData.clientId && (
                        <IconButton onClick={() => handleCopyToClipboard(formData.clientId)} edge="end" size="small" sx={{ ml: 0.5 }}>
                          <ContentCopyIcon />
                        </IconButton>
                      )}
                    </Box>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="App Client Secret"
                type={showClientSecret ? 'text' : 'password'}
                value={formData.clientSecret}
                onChange={handleChange('clientSecret')}
                placeholder="Enter your App Client Secret"
                error={!!formErrors.clientSecret}
                helperText={formErrors.clientSecret}
                InputProps={{
                  startAdornment: <SecurityIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton onClick={() => toggleVisibility('clientSecret')} edge="end" size="small">
                        {showClientSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      {formData.clientSecret && (
                        <IconButton onClick={() => handleCopyToClipboard(formData.clientSecret)} edge="end" size="small" sx={{ ml: 0.5 }}>
                          <ContentCopyIcon />
                        </IconButton>
                      )}
                    </Box>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Tenant ID"
                type={showTenantId ? 'text' : 'password'}
                value={formData.tenantId}
                onChange={handleChange('tenantId')}
                placeholder="Enter your Tenant ID"
                error={!!formErrors.tenantId}
                helperText={formErrors.tenantId}
                InputProps={{
                  startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton onClick={() => toggleVisibility('tenantId')} edge="end" size="small">
                        {showTenantId ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      {formData.tenantId && (
                        <IconButton onClick={() => handleCopyToClipboard(formData.tenantId)} edge="end" size="small" sx={{ ml: 0.5 }}>
                          <ContentCopyIcon />
                        </IconButton>
                      )}
                    </Box>
                  ),
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Sender Email"
                type="email"
                value={formData.senderEmail}
                onChange={handleChange('senderEmail')}
                placeholder="email@yourdomain.com"
                error={!!formErrors.senderEmail}
                helperText={formErrors.senderEmail}
                InputProps={{
                  startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: formData.senderEmail && (
                    <IconButton onClick={() => handleCopyToClipboard(formData.senderEmail)} edge="end" size="small">
                      <ContentCopyIcon />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb', mt: 0 }}>
          <Button onClick={handleCloseModal} color="inherit" sx={{ mr: 1 }}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading}>
            {loading ? 'Configuring...' : 'Configure Outlook'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Details Dialog */}
      <Dialog open={detailsModalOpen} onClose={() => setDetailsModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, borderBottom: '1px solid #e5e7eb', pb: 2, mb: 0 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#16a34a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 20, color: '#ffffff' }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Outlook Connection Details
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Grid container pt={4} spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="App Client ID"
                type={showClientId ? 'text' : 'password'}
                value={emailData?.outlook?.clientId || ''}
                InputProps={{
                  startAdornment: <KeyIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton onClick={() => toggleVisibility('clientId')} edge="end" size="small">
                        {showClientId ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      <IconButton onClick={() => handleCopyToClipboard(emailData?.outlook?.clientId || '')} edge="end" size="small" sx={{ ml: 0.5 }}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Box>
                  ),
                }}
                sx={{ '& .MuiInputBase-input': { color: '#6b7280' } }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="App Client Secret"
                type={showClientSecret ? 'text' : 'password'}
                value={emailData?.outlook?.clientSecret || ''}
                InputProps={{
                  startAdornment: <SecurityIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton onClick={() => toggleVisibility('clientSecret')} edge="end" size="small">
                        {showClientSecret ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      <IconButton onClick={() => handleCopyToClipboard(emailData?.outlook?.clientSecret || '')} edge="end" size="small" sx={{ ml: 0.5 }}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Box>
                  ),
                }}
                sx={{ '& .MuiInputBase-input': { color: '#6b7280' } }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Tenant ID"
                type={showTenantId ? 'text' : 'password'}
                value={emailData?.outlook?.tenantId || ''}
                InputProps={{
                  startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <IconButton onClick={() => toggleVisibility('tenantId')} edge="end" size="small">
                        {showTenantId ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                      <IconButton onClick={() => handleCopyToClipboard(emailData?.outlook?.tenantId || '')} edge="end" size="small" sx={{ ml: 0.5 }}>
                        <ContentCopyIcon />
                      </IconButton>
                    </Box>
                  ),
                }}
                sx={{ '& .MuiInputBase-input': { color: '#6b7280' } }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Sender Email"
                type="email"
                value={emailData?.outlook?.senderEmail || ''}
                InputProps={{
                  startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <IconButton onClick={() => handleCopyToClipboard(emailData?.outlook?.senderEmail || '')} edge="end" size="small">
                      <ContentCopyIcon />
                    </IconButton>
                  ),
                }}
                sx={{ '& .MuiInputBase-input': { color: '#6b7280' } }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid #e5e7eb', mt: 0 }}>
          <Button onClick={() => setDetailsModalOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OutlookIntegration;

